import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "../components/Brand";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success("Bem-vindo(a) ao Pregoeiros Summit!");
      navigate(user.role === "admin" ? "/admin" : "/lobby");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="label-dark" htmlFor="email">E-mail da inscrição</label>
        <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="input-dark" placeholder="seu@email.com" data-testid="login-email-input" />
      </div>
      <div>
        <label className="label-dark" htmlFor="password">Senha</label>
        <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className="input-dark" placeholder="••••••••" data-testid="login-password-input" />
      </div>
      {error && (
        <div className="bg-red-500/10 border border-red-500/40 text-red-300 text-sm rounded-lg px-4 py-3" data-testid="auth-error">
          {error}
        </div>
      )}
      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit-button">
        {busy ? "Aguarde..." : "Acessar Plataforma"}
      </button>
    </form>
  );
}

function ActivateForm() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { activate } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setBusy(true);
    try {
      await activate(email, name, password);
      toast.success("Conta ativada! Bem-vindo(a) ao Pregoeiros Summit!");
      navigate("/lobby");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="text-sm text-slate-300 mb-5" data-testid="first-access-modal">
        Seu e-mail foi inscrito pela organização? Crie sua senha para acessar a transmissão ao vivo.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label-dark" htmlFor="act-email">E-mail da inscrição</label>
          <input id="act-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="input-dark" placeholder="seu@email.com" data-testid="login-email-input" />
        </div>
        <div>
          <label className="label-dark" htmlFor="name">Seu nome completo</label>
          <input id="name" type="text" required value={name} onChange={(e) => setName(e.target.value)}
            className="input-dark" placeholder="Nome que sairá no certificado" data-testid="first-access-name-input" />
        </div>
        <div>
          <label className="label-dark" htmlFor="act-password">Crie uma senha</label>
          <input id="act-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className="input-dark" placeholder="••••••••" data-testid="login-password-input" />
        </div>
        <div>
          <label className="label-dark" htmlFor="confirm">Confirme a senha</label>
          <input id="confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="input-dark" placeholder="••••••••" data-testid="first-access-confirm-input" />
        </div>
        {error && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-300 text-sm rounded-lg px-4 py-3" data-testid="auth-error">
            {error}
          </div>
        )}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit-button">
          {busy ? "Aguarde..." : "Criar senha e acessar"}
        </button>
      </form>
    </>
  );
}

export default function Login() {
  const [params] = useSearchParams();
  const location = useLocation();
  const wantsActivate = location.pathname === "/primeiro-acesso" || params.get("modo") === "primeiro-acesso";
  const [mode, setMode] = useState(wantsActivate ? "activate" : "login");

  return (
    <div className="min-h-screen gradient-brand flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size="lg" /></div>
        <div className="bg-[#101B42]/80 backdrop-blur-xl border border-[#00C8FF]/25 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <div className="grid grid-cols-2 gap-2 mb-6 bg-[#0C1530] rounded-lg p-1">
            <button type="button" onClick={() => setMode("login")} data-testid="tab-login"
              className={`py-2.5 rounded-md text-sm font-bold uppercase tracking-wider transition-colors ${mode === "login" ? "bg-[#F5A623] text-[#0C1530]" : "text-slate-400 hover:text-white"}`}>
              Entrar
            </button>
            <button type="button" onClick={() => setMode("activate")} data-testid="tab-first-access"
              className={`py-2.5 rounded-md text-sm font-bold uppercase tracking-wider transition-colors ${mode === "activate" ? "bg-[#F5A623] text-[#0C1530]" : "text-slate-400 hover:text-white"}`}>
              Primeiro Acesso
            </button>
          </div>
          {mode === "login" ? <LoginForm /> : <ActivateForm />}
        </div>
        <p className="text-center text-xs text-slate-500 mt-6">
          Acesso exclusivo para inscritos na Transmissão ao Vivo.
        </p>
      </div>
    </div>
  );
}
